<script setup lang="ts">
import { computed } from 'vue';
import { gaiaAccent, gaiaFontSize, gaiaFontWeight, gaiaNeutral } from '../../themes/tokens';

const props = defineProps({
  /**
   * The variant's name, exactly as its author wrote it — "German",
   * "Freelancer (Germany)", "Male wording". Rendered verbatim: these are words
   * a person chose, not a code to normalise.
   */
  name: {
    type: String,
    required: true,
  },
  /**
   * Longer names are clipped here and carried in full by the title attribute.
   * The cap is generous enough for a phrase and short enough that one variant
   * can't push a card's status chip off the row.
   */
  maxLength: {
    type: Number,
    default: 28,
  },
});

const clipped = computed<boolean>(() => props.name.length > props.maxLength);
const shown = computed<string>(() =>
  clipped.value ? `${props.name.slice(0, props.maxLength).trimEnd()}…` : props.name
);

const accent = gaiaAccent.base;
const border = gaiaAccent.border;
const surface = gaiaAccent.wash;
// --- chrome (type scale for the scoped styles) ---
const fsMeta = gaiaFontSize.meta;
const weight = String(gaiaFontWeight.semibold);
const emptyColor = gaiaNeutral.textFaint;
</script>

<template>
  <!-- A template variant as templating draws it everywhere (template detail's
       card grid, the variant hero, the drafts list, the Office panel): a
       violet chip, squared rather than pill-round so it reads as a label on
       the document, not a status. The name is prose — sentence case, the UI
       font — since it can just as easily be "Freelancer (Germany)" as "de". -->
  <span class="gaia-variant-chip" :title="clipped ? props.name : undefined">{{ shown }}</span>
</template>

<style scoped>
.gaia-variant-chip {
  display: inline-flex;
  align-items: center;
  flex: none;
  max-width: 100%;
  height: 22px;
  padding: 0 8px;
  border: 1px solid v-bind(border);
  border-radius: 7px;
  background: v-bind(surface);
  font-size: v-bind(fsMeta);
  font-weight: v-bind(weight);
  color: v-bind(accent);
  white-space: nowrap;
}

.gaia-variant-chip:empty::after {
  content: 'Unnamed';
  color: v-bind(emptyColor);
}
</style>
